import React from 'react';
import { useLocation, useNavigate, useSearchParams, matchPath } from 'react-router-dom';
import {useNavigateBack, getLocationReturnPath } from '../../../hooks/useNavigateBack';
import useSWR from 'swr';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import { formatDate as formatDateGlobal } from '../../../config/appConfig';
import { fetcher } from '../../../services/fetcher';
import {
  createSupplyDelivery,
  createSupplyDeliveryFromPurchaseOrder,
  updateSupplyDelivery,
} from '../../../services/mutations';
import {
  Accordion,
  AccordionItem,
  Button,
  Callout,
  Card,
  ChevronLeftIcon,
  DateInput,
  Label,
  NumberInput,
  PlusIcon,
  RefreshIcon,
  Select,
  Textarea,
  TextInput,
  TrashIcon,
} from '../../../components/ui';

import { moduleConfig } from './supplyDeliveriesModuleConfig';
import { moduleConfig as requisitionsModuleConfig } from '../requisitions/requisitionsModuleConfig';
import { formatCaseParenthetical } from '../requisitions/caseDisplay';
import { formLineHasIssue, formLineIsMeaningful } from './deliveryLineIssues';
import SupplyDeliveryShortfallAlert from './SupplyDeliveryShortfallAlert';
import { SupplyDeliveryLine, useSupplyDelivery } from './useData';

type LineForm = {
  key: string;
  level1Filter: string;
  itemId: string;
  itemVcode: string;
  itemDescription: string;
  unitId: string;
  purchaseOrderQty: string;
  deliveryQty: string;
  isDelivered: boolean;
  itemNotes: string;
};

const newLine = (): LineForm => ({
  key: `line-${Date.now()}-${Math.random()}`,
  level1Filter: '',
  itemId: '',
  itemVcode: '',
  itemDescription: '',
  unitId: '',
  purchaseOrderQty: '',
  deliveryQty: '',
  isDelivered: false,
  itemNotes: '',
});

const asNumber = (value: string): number | undefined => {
  if (!value) return undefined;
  const n = Number(value);
  return Number.isNaN(n) ? undefined : n;
};

const normalizeTo2Dp = (value: string): string => {
  if (!value || value.trim() === '') return '';
  const n = Number(value);
  return Number.isFinite(n) ? n.toFixed(2) : value;
};
const asFinite = (value: string): number => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};
const fmt2 = (n: number): string =>
  new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);

const Form: React.FC = () => {
  const { colors } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const goBack = useNavigateBack(moduleConfig.module.route.list, getLocationReturnPath(location.state));
  const [searchParams] = useSearchParams();
  const editMatch = matchPath({ path: '/supplies/supply-deliveries/:id/edit', end: true }, location.pathname);
  const isEdit = editMatch != null;
  const rawId = editMatch?.params.id;
  const deliveryId = rawId != null && /^\d+$/.test(String(rawId)) ? Number(rawId) : undefined;
  const paramCaseId = React.useMemo(() => {
    const e = searchParams.get('caseId');
    const n = e != null ? Number(e) : NaN;
    return Number.isFinite(n) && n > 0 ? Math.floor(n) : undefined;
  }, [searchParams]);
  const paramPoId = React.useMemo(() => {
    const e = searchParams.get('purchaseOrderId');
    const n = e != null ? Number(e) : NaN;
    return Number.isFinite(n) && n > 0 ? Math.floor(n) : undefined;
  }, [searchParams]);

  const { data: existing, isLoading: loadingExisting } = useSupplyDelivery(deliveryId);
  const { data: units = [] } = useSWR<any[]>('/store-units', fetcher);
  const { data: casesRes } = useSWR<any>('/supply-cases?page=1&limit=500', fetcher);
  const { data: suppliersRes } = useSWR<any>('/suppliers?page=1&limit=500', fetcher);
  const { data: vesselsRes } = useSWR<any>('/vessels?page=1&limit=500', fetcher);
  const { data: portsRes } = useSWR<any>('/ports?page=1&limit=500', fetcher);
  const { data: posRes } = useSWR<any>('/supply-purchase-orders?page=1&limit=500', fetcher);

  const cases = React.useMemo(
    () => (Array.isArray(casesRes) ? casesRes : casesRes?.data ?? []),
    [casesRes],
  );
  const suppliers = React.useMemo(
    () => (Array.isArray(suppliersRes) ? suppliersRes : suppliersRes?.data ?? []),
    [suppliersRes],
  );
  const vessels = React.useMemo(
    () => (Array.isArray(vesselsRes) ? vesselsRes : vesselsRes?.data ?? []),
    [vesselsRes],
  );
  const ports = React.useMemo(
    () => (Array.isArray(portsRes) ? portsRes : portsRes?.data ?? []),
    [portsRes],
  );
  const purchaseOrders = React.useMemo(
    () => (Array.isArray(posRes) ? posRes : posRes?.data ?? []),
    [posRes],
  );

  const [selectedCaseId, setSelectedCaseId] = React.useState('');
  const [selectedSupplierId, setSelectedSupplierId] = React.useState('');
  const [selectedVesselId, setSelectedVesselId] = React.useState('');
  const [selectedPoId, setSelectedPoId] = React.useState('');

  const { data: selectedCase } = useSWR<any>(selectedCaseId ? `/supply-cases/${selectedCaseId}` : null, fetcher);
  const { data: selectedPo } = useSWR<any>(selectedPoId ? `/supply-purchase-orders/${selectedPoId}` : null, fetcher);

  const [isSaving, setIsSaving] = React.useState(false);
  const [formData, setFormData] = React.useState({
    dlvDate: '',
    deliveryDate: '',
    ref: '',
    portCode: '',
    refN: '',
    notes: '',
  });
  const [lines, setLines] = React.useState<LineForm[]>([newLine()]);

  const { data: itemsRes } = useSWR<any>('/store-items?page=1&limit=200', fetcher);
  const items = React.useMemo(
    () => (Array.isArray(itemsRes) ? itemsRes : itemsRes?.data ?? []),
    [itemsRes],
  );
  const selectedLevel1Values = React.useMemo(
    () =>
      Array.from(
        new Set(
          lines
            .map((line) => line.level1Filter?.trim())
            .filter((value): value is string => Boolean(value)),
        ),
      ).sort((a, b) => a.localeCompare(b)),
    [lines],
  );
  const { data: levelItemsMap = {} } = useSWR<Record<string, any[]>>(
    selectedLevel1Values.length > 0 ? ['delivery-line-level-items', ...selectedLevel1Values] : null,
    async () => {
      const entries = await Promise.all(
        selectedLevel1Values.map(async (level1) => {
          const params = new URLSearchParams();
          params.set('filters', JSON.stringify({ filters: { level1: [level1] } }));
          const response = await fetcher(`/store-items?${params.toString()}`);
          const levelItems = Array.isArray(response) ? response : response?.data ?? [];
          return [level1, levelItems] as const;
        }),
      );
      return Object.fromEntries(entries);
    },
    { revalidateOnFocus: false },
  );
  const itemsById = React.useMemo(() => {
    const map = new Map<string, any>();
    for (const item of items) {
      map.set(String(item.id), item);
    }
    for (const levelItems of Object.values(levelItemsMap)) {
      for (const item of levelItems) {
        map.set(String(item.id), item);
      }
    }
    return map;
  }, [items, levelItemsMap]);
  const level1Options = React.useMemo(() => {
    const set = new Set<string>();
    for (const it of items) {
      const L = (it.level1 ?? '').trim();
      if (L) set.add(L);
    }
    return [
      { value: '', label: 'All' },
      ...Array.from(set)
        .sort((a, b) => a.localeCompare(b))
        .map((v) => ({ value: v, label: v })),
    ];
  }, [items]);

  const caseOptions = React.useMemo(() => {
    const rows = cases.map((c: any) => ({
      value: String(c.id),
      label: c.ref || `Case #${c.id}`,
    }));
    if (existing?.caseId && existing?.supplyCase && !rows.some((o: any) => o.value === String(existing.caseId))) {
      rows.push({ value: String(existing.caseId), label: existing.supplyCase.ref || `Case #${existing.caseId}` });
    }
    return [{ value: '', label: 'Select case' }, ...rows];
  }, [cases, existing?.caseId, existing?.supplyCase]);

  const supplierOptions = React.useMemo(() => {
    const rows = suppliers.map((s: any) => ({
      value: String(s.id),
      label: s.code ? `${s.name} (${s.code})` : s.name,
    }));
    if (existing?.supplierId && existing?.supplier && !rows.some((o: any) => o.value === String(existing.supplierId))) {
      rows.push({
        value: String(existing.supplierId),
        label: existing.supplier.name || `Supplier #${existing.supplierId}`,
      });
    }
    return [{ value: '', label: 'Select supplier' }, ...rows];
  }, [suppliers, existing?.supplierId, existing?.supplier]);

  const vesselOptions = React.useMemo(() => {
    const rows = vessels.map((v: any) => ({
      value: String(v.id),
      label: v.vesselAbrv ? `${v.vesselName} (${v.vesselAbrv})` : v.vesselName || `Vessel #${v.id}`,
    }));
    if (existing?.vesselId && existing?.vessel && !rows.some((o: any) => o.value === String(existing.vesselId))) {
      rows.push({
        value: String(existing.vesselId),
        label: existing.vessel.vesselName || `Vessel #${existing.vesselId}`,
      });
    }
    return [{ value: '', label: 'Select vessel' }, ...rows];
  }, [vessels, existing?.vesselId, existing?.vessel]);

  const portOptions = React.useMemo(
    () => [
      { value: '', label: 'Port (required)' },
      ...ports.map((p: any) => {
        const portName = (p.name || `Port #${p.id}`).trim();
        const portCode = (p.code || '').trim();
        const countryLabel = (p.country?.name || p.countryName || '').trim();
        const portLabel = portCode ? `${portName} (${portCode})` : portName;
        return {
          value: String(p.id),
          label: countryLabel ? `${portLabel} - ${countryLabel}` : portLabel,
        };
      }),
    ],
    [ports],
  );

  const poOptions = React.useMemo(() => {
    const cid = asNumber(selectedCaseId);
    const sid = asNumber(selectedSupplierId);
    type PoRow = { value: string; label: string };
    let rows: PoRow[] = purchaseOrders.map((po: any) => ({ value: String(po.id), label: po.ref || `PO #${po.id}` }));
    if (cid) {
      rows = rows.filter((o: PoRow) => {
        const po = purchaseOrders.find((p: any) => String(p.id) === o.value);
        return po && po.caseId === cid && (sid ? po.supplierId === sid : true);
      });
    }
    if (existing?.purchaseOrderId && existing?.order && !rows.some((o: PoRow) => o.value === String(existing.purchaseOrderId))) {
      rows.push({ value: String(existing.purchaseOrderId), label: existing.order.ref || `PO #${existing.purchaseOrderId}` });
    }
    return [{ value: '', label: 'No purchase order' }, ...rows];
  }, [purchaseOrders, selectedCaseId, selectedSupplierId, existing?.purchaseOrderId, existing?.order]);

  const getItemOptionsForLine = React.useCallback(
    (line: LineForm) => {
      const f = line.level1Filter?.trim();
      const filtered = f ? levelItemsMap[f] ?? [] : items;
      const rows: { value: string; label: string }[] = filtered.map((it: any) => ({
        value: String(it.id),
        label: `${it.vendorCode || ''} - ${it.name || ''}`,
      }));
      if (line.itemId && !rows.some((o) => o.value === line.itemId)) {
        const idStr = String(line.itemId);
        const label =
          (line.itemVcode && line.itemVcode.trim()) || (line.itemDescription && line.itemDescription.trim())
            ? `${(line.itemVcode || '').trim()} - ${(line.itemDescription || '').trim()}`
            : `Item #${idStr}`;
        rows.push({ value: idStr, label });
      }
      return [{ value: '', label: 'Select item' }, ...rows];
    },
    [items, levelItemsMap],
  );

  const unitOptions = React.useMemo(() => {
    const rows: { value: string; label: string }[] = units.map((unit) => ({
      value: String(unit.id),
      label: unit.ucode || unit.descr || `Unit #${unit.id}`,
    }));
    const seen = new Set(rows.map((o) => o.value));
    for (const line of lines) {
      if (!String(line.unitId).trim()) continue;
      const uStr = String(line.unitId);
      if (seen.has(uStr)) continue;
      seen.add(uStr);
      rows.push({ value: uStr, label: `Unit #${uStr}` });
    }
    return [{ value: '', label: 'Unit' }, ...rows];
  }, [units, lines]);

  React.useEffect(() => {
    if (!isEdit && paramCaseId) setSelectedCaseId(String(paramCaseId));
  }, [isEdit, paramCaseId]);

  React.useEffect(() => {
    if (!isEdit && paramPoId) setSelectedPoId(String(paramPoId));
  }, [isEdit, paramPoId]);

  const poPrefilledRef = React.useRef(false);
  React.useEffect(() => {
    if (isEdit || !selectedPo || poPrefilledRef.current) return;
    if (paramPoId && selectedPo.id === paramPoId) {
      if (selectedPo.caseId) setSelectedCaseId(String(selectedPo.caseId));
      if (selectedPo.supplierId) setSelectedSupplierId(String(selectedPo.supplierId));
      poPrefilledRef.current = true;
    }
  }, [isEdit, paramPoId, selectedPo]);

  React.useEffect(() => {
    if (!selectedCase || !selectedCaseId) return;
    if (selectedCase.vesselId && !isEdit) {
      setSelectedVesselId(String(selectedCase.vesselId));
    }
  }, [selectedCase, selectedCaseId, isEdit]);

  React.useEffect(() => {
    if (!existing) return;
    setSelectedCaseId(String(existing.caseId));
    setSelectedSupplierId(String(existing.supplierId));
    setSelectedVesselId(String(existing.vesselId));
    setSelectedPoId(existing.purchaseOrderId != null ? String(existing.purchaseOrderId) : '');
    setFormData({
      dlvDate: existing.dlvDate ? existing.dlvDate.slice(0, 10) : '',
      deliveryDate: existing.deliveryDate ? existing.deliveryDate.slice(0, 10) : '',
      ref: existing.ref || '',
      portCode: existing.portCode != null ? String(existing.portCode) : '',
      refN: existing.refN != null ? String(existing.refN) : '',
      notes: existing.notes || '',
    });
    const mapped = (existing.lines || []).map((line) => {
      const po = line.purchaseOrderQty != null ? String(line.purchaseOrderQty) : '';
      const isDone = Boolean(line.isDelivered);
      return {
        key: `line-${line.id || Math.random()}`,
        level1Filter: (line.item?.level1 ?? '').trim(),
        itemId: line.itemId ? String(line.itemId) : '',
        itemVcode: line.itemVcode || '',
        itemDescription: line.itemDescription || '',
        unitId: line.unitId ? String(line.unitId) : '',
        purchaseOrderQty: po,
        deliveryQty: isDone ? po : line.deliveryQty != null ? String(line.deliveryQty) : '',
        isDelivered: isDone,
        itemNotes: line.itemNotes || '',
      };
    });
    setLines(mapped.length > 0 ? mapped : [newLine()]);
  }, [existing]);

  const copyLinesFromPurchaseOrder = React.useCallback(() => {
    if (!selectedPo || !Array.isArray(selectedPo.lineItems)) return;
    const mapped = selectedPo.lineItems.map((line: any) => ({
      key: `line-${line.id || Math.random()}`,
      level1Filter: (line.item?.level1 ?? '').trim(),
      itemId: line.itemId ? String(line.itemId) : '',
      itemVcode: line.itemVcode || '',
      itemDescription: line.itemDescr || line.itemDescription || '',
      unitId: line.unitId ? String(line.unitId) : '',
      purchaseOrderQty: line.qty != null ? String(line.qty) : '',
      deliveryQty: line.qty != null ? String(line.qty) : '',
      isDelivered: false,
      itemNotes: line.itemNotes || '',
    }));
    setLines(mapped.length > 0 ? mapped : [newLine()]);
  }, [selectedPo]);
  const editLinesFromOrderPrefilledRef = React.useRef(false);
  React.useEffect(() => {
    if (!isEdit || editLinesFromOrderPrefilledRef.current) return;
    if (!existing?.purchaseOrderId) return;
    if (!selectedPo || !Array.isArray(selectedPo.lineItems)) return;
    copyLinesFromPurchaseOrder();
    editLinesFromOrderPrefilledRef.current = true;
  }, [isEdit, existing?.purchaseOrderId, selectedPo, copyLinesFromPurchaseOrder]);

  const onItemChange = (lineKey: string, itemId: string) => {
    const item = itemsById.get(itemId);
    setLines((prev) =>
      prev.map((line) =>
        line.key !== lineKey
          ? line
          : {
              ...line,
              itemId,
              itemVcode: item?.vendorCode || '',
              itemDescription: item?.name || '',
              unitId: item?.unitId ? String(item.unitId) : '',
              level1Filter: (item?.level1 ?? '').trim(),
            },
      ),
    );
  };
  const onLevel1FilterChange = (lineKey: string, value: string) => {
    setLines((prev) =>
      prev.map((line) => {
        if (line.key !== lineKey) return line;
        const next: LineForm = { ...line, level1Filter: value };
        const f = value.trim();
        const previousLevel1 = (line.level1Filter ?? '').trim();
        if (f && line.itemId && previousLevel1 !== f) {
          next.itemId = '';
          next.itemDescription = '';
          next.itemVcode = '';
          next.unitId = '';
        }
        return next;
      }),
    );
  };

  const normalizedLines: SupplyDeliveryLine[] = React.useMemo(() => {
    return lines
      .map((line) => ({
        itemId: asNumber(line.itemId),
        itemVcode: line.itemVcode || undefined,
        itemDescription: line.itemDescription || undefined,
        unitId: asNumber(line.unitId),
        purchaseOrderQty: asNumber(line.purchaseOrderQty),
        deliveryQty: asNumber(line.deliveryQty),
        isDelivered: line.isDelivered,
        itemNotes: line.itemNotes || undefined,
      }))
      .filter(
        (line) => line.itemId || line.itemDescription || line.purchaseOrderQty != null || line.deliveryQty != null,
      );
  }, [lines]);

  const lineSums = React.useMemo(() => {
    return lines.reduce(
      (acc, line) => {
        acc.purchaseOrderQty += asFinite(line.purchaseOrderQty);
        acc.deliveryQty += asFinite(line.deliveryQty);
        return acc;
      },
      { purchaseOrderQty: 0, deliveryQty: 0 },
    );
  }, [lines]);

  const submit = async (mode: 'full' | 'fromOrderOnly' = 'full') => {
    const caseId = asNumber(selectedCaseId);
    const supplierId = asNumber(selectedSupplierId);
    const vesselId = asNumber(selectedVesselId);
    const hasRequiredDetails = Boolean(formData.dlvDate.trim()) && asNumber(formData.portCode) != null;
    if (!caseId || !supplierId || !vesselId || !hasRequiredDetails) return;
    if (mode === 'fromOrderOnly') {
      const poid = asNumber(selectedPoId);
      if (!poid) return;
      setIsSaving(true);
      try {
        await createSupplyDeliveryFromPurchaseOrder({
          purchaseOrderId: poid,
          vesselId,
          dlvDate: formData.dlvDate || undefined,
          ref: formData.ref || undefined,
          portCode: asNumber(formData.portCode),
          deliveryDate: formData.deliveryDate || undefined,
          refN: asNumber(formData.refN),
          notes: formData.notes || undefined,
        });
        goBack();
      } finally {
        setIsSaving(false);
      }
      return;
    }
    setIsSaving(true);
    try {
      const payload = {
        caseId,
        supplierId,
        vesselId,
        dlvDate: formData.dlvDate || undefined,
        deliveryDate: formData.deliveryDate || undefined,
        ref: formData.ref || undefined,
        portCode: asNumber(formData.portCode),
        purchaseOrderId: asNumber(selectedPoId),
        refN: asNumber(formData.refN),
        notes: formData.notes || undefined,
        lines: normalizedLines,
      };
      if (isEdit && deliveryId) await updateSupplyDelivery(deliveryId, payload);
      else await createSupplyDelivery(payload);
      goBack();
    } finally {
      setIsSaving(false);
    }
  };

  const hasRequiredDeliveryDetails = Boolean(formData.dlvDate.trim()) && asNumber(formData.portCode) != null;
  const canFromOrder = Boolean(
    asNumber(selectedPoId) &&
      asNumber(selectedVesselId) &&
      asNumber(selectedCaseId) &&
      hasRequiredDeliveryDetails,
  );

  const hasFormLineIssues = React.useMemo(() => {
    for (const line of lines) {
      if (!formLineIsMeaningful(line)) continue;
      if (formLineHasIssue(line)) return true;
    }
    return false;
  }, [lines]);

  const newRequisitionNoCaseTo = React.useMemo(() => {
    if (!isEdit || !deliveryId || !hasFormLineIssues) return null;
    const vid = selectedVesselId || (existing?.vesselId != null ? String(existing.vesselId) : '');
    if (!vid) return null;
    const q = new URLSearchParams();
    q.set('noCase', '1');
    q.set('vesselId', vid);
    q.set('importFromDelivery', String(deliveryId));
    return `${requisitionsModuleConfig.module.route.new}?${q.toString()}`;
  }, [isEdit, deliveryId, hasFormLineIssues, selectedVesselId, existing?.vesselId]);
  const vesselLockedByCase = React.useMemo(() => {
    if (!isEdit) return false;
    const caseIdN = asNumber(selectedCaseId);
    return caseIdN != null && caseIdN > 0;
  }, [isEdit, selectedCaseId]);
  const deliveryCaseCaption = React.useMemo(() => {
    if (!isEdit) return null;
    const caseIdN = existing?.caseId ?? null;
    if (caseIdN == null || caseIdN <= 0) return null;
    return (
      formatCaseParenthetical(
        existing?.supplyCase ?? {
          id: caseIdN,
        },
        (iso) => formatDateGlobal(iso || undefined),
      ) ?? null
    );
  }, [isEdit, existing?.caseId, existing?.supplyCase]);
  const deliveryPurchaseOrderCaption = React.useMemo(() => {
    if (!isEdit) return null;
    const ref = existing?.order?.ref?.trim() || (existing?.purchaseOrderId != null ? `PO-${existing.purchaseOrderId}` : '');
    if (!ref) return null;
    return `Purchase order: ${ref}`;
  }, [isEdit, existing?.order?.ref, existing?.purchaseOrderId]);
  const deliverySupplierCaption = React.useMemo(() => {
    if (!isEdit) return null;
    const name = existing?.supplier?.name?.trim() || '';
    const code = existing?.supplier?.code?.trim() || '';
    if (!name && existing?.supplierId == null) return null;
    if (name && code) return `Supplier: ${name} (${code})`;
    if (name) return `Supplier: ${name}`;
    return `Supplier: #${existing?.supplierId}`;
  }, [isEdit, existing?.supplier?.name, existing?.supplier?.code, existing?.supplierId]);
  const deliveryVesselCaption = React.useMemo(() => {
    if (!isEdit) return null;
    const vesselName = existing?.vessel?.vesselName?.trim() || '';
    if (vesselName) return `Vessel: ${vesselName}`;
    if (existing?.vesselId != null) return `Vessel: #${existing.vesselId}`;
    return null;
  }, [isEdit, existing?.vessel?.vesselName, existing?.vesselId]);
  const shouldLockOrderLineFields = React.useCallback(
    (line: LineForm) => {
      const caseIdN = asNumber(selectedCaseId);
      const orderIdN = asNumber(selectedPoId);
      if (!caseIdN || caseIdN <= 0) return false;
      if (!orderIdN || orderIdN <= 0) return false;
      // Order-origin lines carry purchase order quantity on this form.
      return line.purchaseOrderQty !== '';
    },
    [selectedCaseId, selectedPoId],
  );

  

  return (
    <form
      className="space-y-5"
      onSubmit={(e) => {
        e.preventDefault();
        void submit('full');
      }}
    >
      <Card
        variant="outlined"
        className={cn('flex flex-wrap items-center justify-between gap-3 px-4 py-3 sm:px-5 sm:py-3.5', 'border shadow-sm')}
      >
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className={cn('-ml-2 gap-1.5 font-medium', colors.text)}
          onClick={goBack}
        >
          <ChevronLeftIcon className="h-4 w-4 opacity-80" />
          Back
        </Button>
        <div className="flex flex-wrap items-center gap-2">
          {!isEdit && asNumber(selectedPoId) ? (
            <Button
              type="button"
              variant="outline"
              disabled={isSaving || loadingExisting || !canFromOrder}
              loading={isSaving}
              onClick={() => void submit('fromOrderOnly')}
            >
              Create from order (lines from PO)
            </Button>
          ) : null}
          <Button
            type="submit"
            disabled={
              isSaving ||
              loadingExisting ||
              !asNumber(selectedCaseId) ||
              !asNumber(selectedSupplierId) ||
              !asNumber(selectedVesselId) ||
              !hasRequiredDeliveryDetails
            }
            loading={isSaving}
            size="default"
          >
            {isSaving ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </Card>

      {deliveryCaseCaption ? (
        <Callout variant="success" title="Linked supply case">
              <div className="mt-1 flex flex-wrap items-center gap-2">
                <span
                  className={cn(
                    'inline-flex items-center rounded-lg border px-2.5 py-1 text-sm',
                    'border-green-200/90 bg-green-100/70 text-green-950 dark:border-green-700/60 dark:bg-green-900/40 dark:text-green-100',
                  )}
                >
                  {deliveryCaseCaption}
                </span>
                {deliveryPurchaseOrderCaption ? (
                  <span
                    className={cn(
                      'inline-flex items-center rounded-lg border px-2.5 py-1 text-sm font-medium',
                      'border-indigo-200/90 bg-indigo-50/90 text-indigo-900 dark:border-indigo-700/60 dark:bg-indigo-950/40 dark:text-indigo-200',
                    )}
                  >
                    {deliveryPurchaseOrderCaption}
                  </span>
                ) : null}
                {deliveryVesselCaption ? (
                  <span
                    className={cn(
                      'inline-flex items-center rounded-lg border px-2.5 py-1 text-sm font-medium',
                      'border-indigo-200/90 bg-indigo-50/90 text-indigo-900 dark:border-indigo-700/60 dark:bg-indigo-950/40 dark:text-indigo-200',
                    )}
                  >
                    {deliveryVesselCaption}
                  </span>
                ) : null}
                {deliverySupplierCaption ? (
                  <span
                    className={cn(
                      'inline-flex items-center rounded-lg border px-2.5 py-1 text-sm font-medium',
                      'border-indigo-200/90 bg-indigo-50/90 text-indigo-900 dark:border-indigo-700/60 dark:bg-indigo-950/40 dark:text-indigo-200',
                    )}
                  >
                    {deliverySupplierCaption}
                  </span>
                ) : null}
              </div>
            </Callout>
      ) : null}

      {isEdit && newRequisitionNoCaseTo ? (
        <SupplyDeliveryShortfallAlert requisitionTo={newRequisitionNoCaseTo} />
      ) : null}

      <Card variant="default" className="p-4 md:p-5">
        <div className="mb-4">
          <h2 className={cn('text-sm font-semibold uppercase tracking-wide', colors.textSecondary)}>Delivery</h2>
        </div>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-12">
          <div className="md:col-span-3">
            <Label htmlFor="dlv-date" required>Dlv. date</Label>
            <DateInput
              id="dlv-date"
              value={formData.dlvDate}
              onChange={(e) => setFormData((s) => ({ ...s, dlvDate: e.target.value }))}
              placeholder="Select date"
              showFormatHint={false}
            />
          </div>
          <div className="md:col-span-3">
            <Label htmlFor="dlv-delivery">Arrival / delivery</Label>
            <DateInput
              id="dlv-delivery"
              value={formData.deliveryDate}
              onChange={(e) => setFormData((s) => ({ ...s, deliveryDate: e.target.value }))}
              placeholder="Select date"
              showFormatHint={false}
            />
          </div>
          <div className="md:col-span-2">
            <Label htmlFor="dlv-ref">Reference</Label>
            <TextInput
              id="dlv-ref"
              value={formData.ref}
              onChange={(e) => setFormData((s) => ({ ...s, ref: e.target.value }))}
              placeholder="Reference"
            />
          </div>
          {!isEdit ? (
            <div className="md:col-span-2">
              <Label htmlFor="dlv-refn">Ref #</Label>
              <NumberInput
                id="dlv-refn"
                value={formData.refN}
                onChange={(e) => setFormData((s) => ({ ...s, refN: e.target.value == null ? '' : String(e.target.value) }))}
                showClearButton={false}
                placeholder="No."
              />
            </div>
          ) : null}
          <div className="md:col-span-2">
            <Label htmlFor="dlv-port" required>Port</Label>
            <Select
              id="dlv-port"
              value={formData.portCode}
              onChange={(e) => setFormData((s) => ({ ...s, portCode: e.target.value }))}
              options={portOptions}
              showClearButton={false}
            />
          </div>
          {!isEdit ? (
            <>
              <div className="md:col-span-3">
                <Label htmlFor="dlv-case">Supply case</Label>
                <Select
                  id="dlv-case"
                  value={selectedCaseId}
                  onChange={(e) => setSelectedCaseId(e.target.value)}
                  options={caseOptions}
                  placeholder="Select case"
                  showClearButton={false}
                />
              </div>
              <div className="md:col-span-3">
                <Label htmlFor="dlv-supplier">Supplier</Label>
                <Select
                  id="dlv-supplier"
                  value={selectedSupplierId}
                  onChange={(e) => setSelectedSupplierId(e.target.value)}
                  options={supplierOptions}
                  placeholder="Select supplier"
                  showClearButton={false}
                />
              </div>
              <div className="md:col-span-3">
                <Label htmlFor="dlv-vessel">Vessel</Label>
                <Select
                  id="dlv-vessel"
                  value={selectedVesselId}
                  onChange={(e) => setSelectedVesselId(e.target.value)}
                  options={vesselOptions}
                  placeholder="Select vessel"
                  showClearButton={false}
                  disabled={vesselLockedByCase}
                />
              </div>
              <div className="md:col-span-3">
                <Label htmlFor="dlv-po">Purchase order</Label>
                <Select
                  id="dlv-po"
                  value={selectedPoId}
                  onChange={(e) => setSelectedPoId(e.target.value)}
                  options={poOptions}
                  placeholder="Optional"
                  showClearButton={false}
                />
              </div>
            </>
          ) : null}
          <div className="md:col-span-12">
            <Label htmlFor="dlv-notes">Notes</Label>
            <Textarea
              id="dlv-notes"
              value={formData.notes}
              onChange={(e) => setFormData((s) => ({ ...s, notes: e.target.value }))}
              rows={2}
              showClearButton={false}
              className="min-h-[72px]"
            />
          </div>
          {!isEdit ? (
            <div className="md:col-span-12">
              <div className={cn('rounded-lg border p-3 text-sm', colors.border, colors.surfaceHover)}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className={cn('font-medium', colors.text)}>Copy lines from selected purchase order</span>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={!selectedPoId || !selectedPo}
                    onClick={copyLinesFromPurchaseOrder}
                    className="gap-1.5"
                  >
                    <RefreshIcon className="h-4 w-4" />
                    Copy items
                  </Button>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </Card>

      <Card variant="default" className="p-4 md:p-5">
        <Accordion className="space-y-3">
          <AccordionItem
            title="Lines"
            defaultOpen
            className={cn('overflow-hidden', colors.border, 'border')}
            headerClassName={cn('!gap-2', colors.surfaceHover)}
          >
            <div className="mb-3 flex justify-end">
              <Button
                type="button"
                variant="outline"
                size="icon"
                title="Add line"
                aria-label="Add line"
                onClick={() => setLines((prev) => [...prev, newLine()])}
              >
                <PlusIcon className="h-5 w-5" />
              </Button>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className={cn('border-b', colors.border)}>
                    <th className="w-[10rem] min-w-[8rem] px-2 py-2 text-left text-xs font-medium">Level 1</th>
                    <th className="min-w-[12rem] px-2 py-2 text-left text-xs font-medium">Item</th>
                    <th className="w-[6.5rem] px-2 py-2 text-right text-xs font-medium">Unit</th>
                    <th className="w-24 px-2 py-2 text-right text-xs font-medium">PO qty</th>
                    <th className="w-24 px-2 py-2 text-right text-xs font-medium">Dlv qty</th>
                    <th className="w-16 px-2 py-2 text-left text-xs font-medium">Done</th>
                    <th className="w-[10rem] px-2 py-2 text-left text-xs font-medium">Notes</th>
                    <th className="px-2 py-2 text-right text-xs font-medium"> </th>
                  </tr>
                </thead>
                <tbody>
                  {lines.map((line) => {
                    const lockOrderFields = shouldLockOrderLineFields(line);
                    return (
                    <tr key={line.key} className="align-top">
                      <td className="p-2">
                        <Select
                          value={line.level1Filter}
                          onChange={(e) => onLevel1FilterChange(line.key, e.target.value)}
                          options={level1Options}
                          placeholder="Level 1"
                          showClearButton={false}
                          className="h-10 min-h-[2.5rem] w-full text-sm"
                          disabled={lockOrderFields}
                        />
                      </td>
                      <td className="p-2">
                        <Select
                          value={line.itemId}
                          onChange={(e) => onItemChange(line.key, e.target.value)}
                          options={getItemOptionsForLine(line)}
                          placeholder="Select item"
                          showClearButton={false}
                          className="h-10 min-h-[2.5rem] w-full min-w-[12rem] text-sm"
                          disabled={lockOrderFields}
                        />
                      </td>
                      <td className="p-2 text-right">
                        <Select
                          value={line.unitId}
                          onChange={(e) => setLines((prev) => prev.map((r) => (r.key === line.key ? { ...r, unitId: e.target.value } : r)))}
                          options={unitOptions}
                          placeholder="Unit"
                          showClearButton={false}
                          className="h-10 min-h-[2.5rem] text-right"
                          disabled={lockOrderFields}
                        />
                      </td>
                      <td className="p-2 text-right">
                        <NumberInput
                          size="sm"
                          value={line.purchaseOrderQty}
                          onChange={(e) => {
                            const v = e.target.value == null ? '' : String(e.target.value);
                            setLines((prev) =>
                              prev.map((r) => {
                                if (r.key !== line.key) return r;
                                const next: LineForm = { ...r, purchaseOrderQty: v };
                                if (r.isDelivered) next.deliveryQty = v;
                                return next;
                              }),
                            );
                          }}
                          showClearButton={false}
                          placeholder="PO"
                          className="h-10 min-h-[2.5rem] box-border py-2 leading-normal text-right"
                        />
                      </td>
                      <td className="p-2 text-right">
                        <NumberInput
                          size="sm"
                          value={line.deliveryQty}
                          disabled={line.isDelivered}
                          title={
                            line.isDelivered
                              ? 'When marked done, delivery qty matches PO qty. Uncheck done to edit.'
                              : undefined
                          }
                          onChange={(e) =>
                            setLines((prev) =>
                              prev.map((r) =>
                                r.key === line.key ? { ...r, deliveryQty: e.target.value == null ? '' : String(e.target.value) } : r,
                              ),
                            )
                          }
                          showClearButton={false}
                          placeholder="Qty"
                          className="h-10 min-h-[2.5rem] box-border py-2 leading-normal text-right"
                        />
                      </td>
                      <td className="p-2 pt-3">
                        <input
                          type="checkbox"
                          className="h-4 w-4 rounded border-gray-300"
                          checked={line.isDelivered}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            setLines((prev) =>
                              prev.map((r) => {
                                if (r.key !== line.key) return r;
                                if (checked) {
                                  return { ...r, isDelivered: true, deliveryQty: r.purchaseOrderQty };
                                }
                                return { ...r, isDelivered: false };
                              }),
                            );
                          }}
                          title="Mark as delivered (sets Dlv qty = PO qty)"
                        />
                      </td>
                      <td className="p-2">
                        <TextInput
                          size="sm"
                          value={line.itemNotes}
                          onChange={(e) =>
                            setLines((prev) => prev.map((r) => (r.key === line.key ? { ...r, itemNotes: e.target.value } : r)))
                          }
                          showClearButton={false}
                          placeholder="Notes"
                          className="h-10 min-h-[2.5rem]"
                        />
                      </td>
                      <td className="p-2 text-right">
                        <Button
                          type="button"
                          variant="danger"
                          size="icon"
                          onClick={() => setLines((prev) => (prev.length > 1 ? prev.filter((r) => r.key !== line.key) : [newLine()]))}
                        >
                          <TrashIcon className="h-5 w-5" />
                        </Button>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className={cn('border-t', colors.border)}>
                    <td colSpan={3} className={cn('px-2 py-2 text-right text-xs font-semibold', colors.textSecondary)}>
                      SUM
                    </td>
                    <td className="px-2 py-2">
                      <div className={cn('text-[0.7rem] font-semibold uppercase text-right', colors.textSecondary)}>PO qty SUM</div>
                      <div className={cn('text-sm font-semibold tabular-nums text-right', colors.text)}>{fmt2(lineSums.purchaseOrderQty)}</div>
                    </td>
                    <td className="px-2 py-2">
                      <div className={cn('text-[0.7rem] font-semibold uppercase text-right', colors.textSecondary)}>Dlv qty SUM</div>
                      <div className={cn('text-sm font-semibold tabular-nums text-right', colors.text)}>{fmt2(lineSums.deliveryQty)}</div>
                    </td>
                    <td />
                    <td />
                    <td />
                  </tr>
                </tfoot>
              </table>
            </div>
          </AccordionItem>
        </Accordion>
      </Card>
    </form>
  );
};

export default Form;
